import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getImageUrl } from '../utils/cdnUtils'
import { getRoundHuayTypeImage, type LotteryRound, type PublicHuayType, lotteryService } from '../services/lotteryService'
import { getTenantIdFromConfig } from '../utils/tenantUtils'
import { useTenantThemeConfig } from '../hooks/useTenantThemeConfig'
import axios from 'axios'
import { withApiBaseUrl } from '../lib/apiBaseUrl'
import RulesGuideModal from '../components/RulesGuideModal'

interface BettingPackage {
  code: string
  name: string
  nameEn?: string
  description?: string
  category?: string
  packageType: 'RATE_BASED' | 'SET_BASED'
  isDefault: boolean
  displayOrder: number
  setConfig?: {
    fixedPrice: number
    fixedPayout: number
    discount: number
    includedPrizeTypes: Array<{
      prizeTypeCode: string
      prizeTypeName: string
      digitCount: number
    }>
  }
  prizeConfigs?: Array<{
    prizeTypeCode: string
    prizeTypeName: string
    payoutRate: number
    discount: number
    minBet?: number
    maxBet?: number
  }>
  metadata?: {
    displayCard?: {
      title?: string
      payoutAmount?: number
      discountPercent?: number
    }
  }
}

const PRIZE_TYPE_LABELS: Record<string, string> = {
  '2_DIGIT_TOP': '2 ตัวบน',
  '2_DIGIT_BOTTOM': '2 ตัวล่าง',
  '2_DIGIT_TOD': '2 ตัวโต๊ด',
  '2_DIGIT_REVERSE': '2 ตัวกลับ',
  '2_DIGIT_FRONT': '2 ตัวหน้า',
  '2_DIGIT_BACK': '2 ตัวหลัง',
  '3_DIGIT_TOP': '3 ตัวบน',
  '3_DIGIT_BOTTOM': '3 ตัวล่าง',
  '3_DIGIT_TOD': '3 ตัวโต๊ด',
  '3_DIGIT_REVERSE': '3 ตัวบน (กลับเลข)',
  'RUNNING_TOP': 'วิ่งบน',
  'RUNNING_BOTTOM': 'วิ่งล่าง',
  '4_DIGIT_TOP': '4 ตัวบน',
  '4_DIGIT_TOD': '4 ตัวโต๊ด',
  '5_DIGIT_TOD': '5 ตัวโต๊ด',
  'FIXED_POSITION_ONES': 'ปักหลักหน่วย',
  'FIXED_POSITION_TENS': 'ปักหลักสิบ',
  'FIXED_POSITION_HUNDREDS': 'ปักหลักร้อย',
  'FIRST_PRIZE': 'รางวัลที่ 1',
  'SECOND_PRIZE': 'รางวัลที่ 2',
  'THIRD_PRIZE': 'รางวัลที่ 3',
  'REVERSE_2_DIGIT': '+2 กลับ',
  'REVERSE_3_DIGIT': '+3 กลับ',
  'TONG_PRIZE': '+ ทอง',
}

function resolvePrizeTypeLabel(prizeTypeCode?: string, prizeTypeName?: string): string {
  const explicitName = String(prizeTypeName || '').trim()
  if (explicitName) return explicitName

  const normalizedCode = String(prizeTypeCode || '').trim().toUpperCase()
  if (!normalizedCode) return ''

  return PRIZE_TYPE_LABELS[normalizedCode] || normalizedCode.replace(/_/g, ' ')
}

function resolvePackageDisplayCard(pkg: BettingPackage) {
  const override = pkg.metadata?.displayCard
  const hasCustomDisplay =
    Boolean(String(override?.title || '').trim())
    || typeof override?.payoutAmount === 'number'
    || typeof override?.discountPercent === 'number'

  if (hasCustomDisplay) {
    return {
      mode: 'custom' as const,
      title: String(override?.title || '').trim() || pkg.name || '',
      payoutAmount: Number(override?.payoutAmount ?? 0),
      discountPercent: Number(override?.discountPercent ?? 0),
    }
  }

  if (pkg.packageType === 'SET_BASED' && pkg.setConfig) {
    return {
      mode: 'set' as const,
      fixedPrice: Number(pkg.setConfig.fixedPrice ?? 0),
      fixedPayout: pkg.setConfig.fixedPayout != null ? Number(pkg.setConfig.fixedPayout) : null,
      discountPercent: Number(pkg.setConfig.discount ?? 0),
    }
  }

  if (pkg.prizeConfigs && pkg.prizeConfigs.length > 0) {
    const firstPrize = pkg.prizeConfigs[0]
    return {
      mode: 'rate' as const,
      title: resolvePrizeTypeLabel(firstPrize.prizeTypeCode, firstPrize.prizeTypeName),
      payoutAmount: Number(firstPrize.payoutRate ?? 0),
      discountPercent: Number(firstPrize.discount ?? 0),
    }
  }

  return {
    mode: 'empty' as const,
  }
}

function buildPackageIdentityHint(pkg: BettingPackage, duplicateNameCount: number): string {
  const description = String(pkg.description || '').trim()
  const code = String(pkg.code || '').trim()
  const displayCard = resolvePackageDisplayCard(pkg)
  const displayTitle =
    displayCard.mode === 'rate' || displayCard.mode === 'custom'
      ? String(displayCard.title || '').trim()
      : ''

  if (duplicateNameCount > 1) {
    if (description) return description
    if (displayTitle && displayTitle !== pkg.name) return displayTitle
    if (code) return code
  }

  if (description && (code.includes('-') || description.length <= 40)) {
    return description
  }

  return ''
}

function isSetLottery(round?: LotteryRound): boolean {
  if (!round) return false
  const combined = [
    round.huayTypeName,
    round.huayTypeCode,
    round.huayName,
    round.huayCode,
  ]
    .filter(Boolean)
    .join(' ')
  return /หวยชุด|เลขชุด|ชุด/i.test(combined)
}

function getSetPackageFamilyCode(code?: string): string {
  return String(code || '')
    .trim()
    .toUpperCase()
    .replace(/_(LARGE|MEDIUM|SMALL)$/i, '')
}

function getPreferredSetFamilies(round?: LotteryRound): string[] {
  const combined = [
    round?.huayTypeName,
    round?.huayTypeCode,
    round?.huayName,
    round?.huayCode,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  if (/รัฐบาลไทย|governmentthai|setgovernmentthai/.test(combined)) {
    return ['THAI_GOV_SET']
  }
  if (/ฮานอย|hanoi/.test(combined)) {
    return ['HANOI_SET']
  }
  if (/ลาว|lao/.test(combined)) {
    return ['LAO_SET']
  }

  return []
}

function resolvePreferredSetPackages(incomingPackages: BettingPackage[], round?: LotteryRound): BettingPackage[] {
  const setPackages = incomingPackages.filter((pkg) => pkg.packageType === 'SET_BASED' && pkg.setConfig)

  if (setPackages.length <= 3) {
    return [...setPackages].sort((a, b) => (b.setConfig?.fixedPrice || 0) - (a.setConfig?.fixedPrice || 0))
  }

  const groupedFamilies = new Map<string, BettingPackage[]>()
  setPackages.forEach((pkg) => {
    const familyCode = getSetPackageFamilyCode(pkg.code)
    if (!familyCode) return
    const existing = groupedFamilies.get(familyCode) || []
    existing.push(pkg)
    groupedFamilies.set(familyCode, existing)
  })

  if (groupedFamilies.size <= 1) {
    return [...setPackages].sort((a, b) => (b.setConfig?.fixedPrice || 0) - (a.setConfig?.fixedPrice || 0))
  }

  const preferredFamilies = getPreferredSetFamilies(round)
  const rankedFamilies = [...groupedFamilies.entries()].map(([familyCode, packages]) => ({
    familyCode,
    packages,
    preferredIndex: preferredFamilies.indexOf(familyCode),
    totalPayout: packages.reduce((sum, pkg) => sum + Number(pkg.setConfig?.fixedPayout || 0), 0),
    totalDiscount: packages.reduce((sum, pkg) => sum + Number(pkg.setConfig?.discount || 0), 0),
  }))

  rankedFamilies.sort((left, right) => {
    const leftPreferred = left.preferredIndex === -1 ? Number.MAX_SAFE_INTEGER : left.preferredIndex
    const rightPreferred = right.preferredIndex === -1 ? Number.MAX_SAFE_INTEGER : right.preferredIndex

    if (leftPreferred !== rightPreferred) return leftPreferred - rightPreferred
    if (left.packages.length !== right.packages.length) return right.packages.length - left.packages.length
    if (left.totalPayout !== right.totalPayout) return right.totalPayout - left.totalPayout
    if (left.totalDiscount !== right.totalDiscount) return left.totalDiscount - right.totalDiscount
    return left.familyCode.localeCompare(right.familyCode)
  })

  const selectedFamily = rankedFamilies[0]
  const familyPackages = selectedFamily?.packages || setPackages
  return [...familyPackages].sort((a, b) => (b.setConfig?.fixedPrice || 0) - (a.setConfig?.fixedPrice || 0))
}

const CommonTermsPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { themeConfig } = useTenantThemeConfig()
  const round = location.state?.round as LotteryRound | undefined
  const tenantId = getTenantIdFromConfig(themeConfig)
  
  const [showModal, setShowModal] = useState(false)
  const [packages, setPackages] = useState<BettingPackage[]>([])
  const [huayTypes, setHuayTypes] = useState<PublicHuayType[]>([])
  const [loading, setLoading] = useState(true)
  const loadPackagesRef = useRef<() => Promise<void>>()

  // Store loadPackages function in ref so WebSocket can access it
  useEffect(() => {
    loadPackagesRef.current = loadPackages
  }, [round])

  useEffect(() => {
    loadPackages()
  }, [round])

  useEffect(() => {
    let cancelled = false
    const loadHuayTypes = async () => {
      try {
        const list = await lotteryService.getTenantHuayTypes({ limit: 100 })
        if (!cancelled) setHuayTypes(list)
      } catch (error) {
        console.error('❌ Error loading huay types for terms page:', error)
      }
    }
    loadHuayTypes()
    return () => {
      cancelled = true
    }
  }, [])

  // WebSocket listener for real-time package updates
  useEffect(() => {
    if (!tenantId) return

    const handlePackageUpdate = (event: Event) => {
      const customEvent = event as CustomEvent
      const data = customEvent.detail

      // Check if this is a betting package update
      if (data?.contentType === 'betting_packages' || 
          data?.contentType === 'betting-packages' ||
          data?.type === 'betting_package_updated' ||
          data?.type === 'package_updated' ||
          data?.content?.type?.includes('betting_package')) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 Betting package update received via WebSocket, reloading packages...', data)
        }
        
        // Reload packages when update is received (with small delay to ensure backend is ready)
        if (loadPackagesRef.current) {
          setTimeout(() => {
            if (loadPackagesRef.current) {
              loadPackagesRef.current()
            }
          }, 500) // 500ms delay to ensure backend changes are committed
        }
      }
    }

    // Listen for content_update events from WebSocket (dispatched by useSmartWebSocket)
    window.addEventListener('content_update', handlePackageUpdate as EventListener)
    
    // Also listen for custom betting package events (dispatched by useSmartWebSocket)
    window.addEventListener('betting_package_updated', handlePackageUpdate as EventListener)
    window.addEventListener('package_updated', handlePackageUpdate as EventListener)

    return () => {
      window.removeEventListener('content_update', handlePackageUpdate as EventListener)
      window.removeEventListener('betting_package_updated', handlePackageUpdate as EventListener)
      window.removeEventListener('package_updated', handlePackageUpdate as EventListener)
    }
  }, [tenantId])

  const resolvePackagesForRound = (incomingPackages: BettingPackage[]): BettingPackage[] => {
    if (!isSetLottery(round)) return incomingPackages

    return resolvePreferredSetPackages(incomingPackages, round)
  }

  const loadPackages = async () => {
    try {
      setLoading(true)
      
      // Get access token for authenticated requests
      const accessToken = localStorage.getItem('accessToken')
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId
      }
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`
      }
      
      if (!round?.huayId) {
        setPackages([])
        return
      }

      const response = await axios.get(
        withApiBaseUrl(`/api/v1/lottery/tenant/${tenantId}/huays/${round.huayId}/packages`),
        { headers }
      )

      if (response.data.success && response.data.data) {
        const packagesData = response.data.data.packages || []
        const resolvedPackages = resolvePackagesForRound(packagesData)

        resolvedPackages.sort((a: any, b: any) => {
          const priceA = a.setConfig?.fixedPrice || a.prizeConfigs?.[0]?.payoutRate || 0
          const priceB = b.setConfig?.fixedPrice || b.prizeConfigs?.[0]?.payoutRate || 0
          return priceB - priceA
        })

        setPackages(resolvedPackages)
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Loaded huay/type packages:', resolvedPackages.length)
        }
        return
      }

      setPackages([])
    } catch (error: any) {
      console.error('❌ Error loading packages:', error)
      setPackages([])
    } finally {
      setLoading(false)
    }
  }

  const handlePackageSelect = (pkg: BettingPackage) => {
    if (round) {
      if (isSetLottery(round) && pkg.packageType === 'SET_BASED') {
        navigate('/set-lotto-game', {
          state: {
            round,
            package: pkg,
            backTo: '/lottery-terms',
            backLabel: 'กลับเลือกแพ็กเกจ',
          },
        })
        return
      }
      // If round exists, navigate to betting page with round info
      navigate('/lottery-bet', {
        state: {
          round,
          package: pkg,
          roundId: round._id
        }
      })
    } else {
      // If no round, navigate to lotto page to select round first
      navigate('/lotto', { 
        state: { 
          selectedPackage: pkg
        } 
      })
    }
  }

  const roundImage = useMemo(() => {
    const image = getRoundHuayTypeImage(round, huayTypes)
    if (image) return image
    return getImageUrl('flag/th.png')
  }, [round, huayTypes])

  const packageNameCounts = useMemo(() => {
    const counts = new Map<string, number>()
    packages.forEach((pkg) => {
      const key = String(pkg.name || '').trim().toLowerCase()
      if (!key) return
      counts.set(key, (counts.get(key) || 0) + 1)
    })
    return counts
  }, [packages])

  const betEndDate = round ? new Date(round.betEndTime) : null
  const closeTime = betEndDate ? betEndDate.toLocaleString('th-TH', { 
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }) : null

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                {/* Header */}
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a 
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/lotto')
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าหลัก
                    </a>

                    <a
                      href="#"
                      className="bdr-5 c-white bgg-4 txt-link px-2"
                      onClick={(e) => {
                        e.preventDefault()
                        setShowModal(true)
                      }}
                    >
                      กติกา & วิธีเล่น
                    </a>
                  </div>
                </div>

                {/* Round Info */}
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    {round ? (
                      <div className="bdr-5 bgc-4 c-red px-2 py-1 wrap justify-content-between">
                        <div className="wrap">
                          <div className="icon-f">
                            <img src={/^https?:\/\//i.test(roundImage) ? roundImage : getImageUrl(roundImage)} alt={round.huayName || ''} />
                          </div>
                          <p className="mx-2 fs-18 t-medium">{round.huayName}</p>
                        </div>
                        {closeTime && (
                          <div className="bgc-9 bdr-5 py-1 px-2 c-white wrap justify-content-center">
                            <small>ปิดรับ {closeTime}</small>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bdr-5 bgc-4 c-red px-2 py-1 wrap justify-content-center">
                        <p className="mx-2 fs-18 t-medium">แพ็คเกจการแทง</p>
                      </div>
                    )}

                    {/* Package Selection */}
                    {loading ? (
                      <div className="text-center py-5">
                        <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                        <p className="c-white mt-2">กำลังโหลดแพ็คเกจ...</p>
                      </div>
                    ) : packages.length === 0 ? (
                      <div className="text-center py-5">
                        <p className="c-white">ไม่พบแพ็คเกจการแทง</p>
                      </div>
                    ) : (
                      <div className="row mx--1">
                        {packages.map((pkg) => {
                          const duplicateNameCount = packageNameCounts.get(String(pkg.name || '').trim().toLowerCase()) || 0
                          const packageIdentityHint = buildPackageIdentityHint(pkg, duplicateNameCount)

                          return (
                          <div key={pkg.code} className="col-12 col-md-4 px-1 mt-2 d-flex">
                            <a
                              href="#"
                              className="bgg-1 p-1 bdr-5 text-center box-hover d-flex flex-column h-100 w-100"
                              onClick={(e) => {
                                e.preventDefault()
                                handlePackageSelect(pkg)
                              }}
                            >
                              <div
                                className="bgc-4 bdr-5 p-2 t-regular c-red d-flex flex-column justify-content-center"
                                style={{ minHeight: '76px' }}
                              >
                                <h5
                                  className="mb-0"
                                  style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                  title={pkg.name}
                                >
                                  {pkg.name}
                                </h5>
                                {packageIdentityHint && (
                                  <small
                                    className="mt-2 px-2 py-1 bdr-5 align-self-center"
                                    style={{
                                      display: 'inline-block',
                                      maxWidth: '100%',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      backgroundColor: 'rgba(165, 24, 24, 0.10)',
                                      color: '#a51818',
                                      opacity: 0.9
                                    }}
                                    title={packageIdentityHint}
                                  >
                                    {packageIdentityHint}
                                  </small>
                                )}
                              </div>
                              <div className="px-1 py-2 c-white d-flex flex-column justify-content-center flex-grow-1">
                                {(() => {
                                  const displayCard = resolvePackageDisplayCard(pkg)
                                  // console.log(`📦 Package ${pkg.code}:`, {
                                  //   packageType: pkg.packageType,
                                  //   hasSetConfig: !!pkg.setConfig,
                                  //   hasPrizeConfigs: !!pkg.prizeConfigs,
                                  //   prizeConfigsLength: pkg.prizeConfigs?.length || 0,
                                  //   prizeConfigs: pkg.prizeConfigs
                                  // })
                                  
                                  if (displayCard.mode === 'custom') {
                                    return (
                                      <div className="mt-2">
                                        <h5>{displayCard.title}</h5>
                                        <h6 className="c-main wrap align-items-start justify-content-center">
                                          จ่าย
                                          <span className="txt-st t-medium px-2">{displayCard.payoutAmount}</span>
                                          บาท
                                        </h6>
                                        <h5>ลด {displayCard.discountPercent}%</h5>
                                      </div>
                                    )
                                  } else if (displayCard.mode === 'set') {
                                    return (
                                      <div className="mt-2">
                                        <h6 className="c-main wrap align-items-start justify-content-center">
                                          ราคา
                                          <span className="txt-st t-medium px-2">
                                            {displayCard.fixedPrice.toLocaleString()}
                                          </span>
                                          บาท
                                        </h6>
                                        {displayCard.fixedPayout != null && (
                                          <h6 className="c-main">
                                            รางวัล {displayCard.fixedPayout.toLocaleString()} บาท
                                          </h6>
                                        )}
                                      </div>
                                    )
                                  } else if (displayCard.mode === 'rate') {
                                    return (
                                      <div className="mt-2">
                                        <h5>{displayCard.title}</h5>
                                        <h6 className="c-main wrap align-items-start justify-content-center">
                                          จ่าย
                                          <span className="txt-st t-medium px-2">{displayCard.payoutAmount}</span>
                                          บาท
                                        </h6>
                                        <h5>ลด {displayCard.discountPercent}%</h5>
                                      </div>
                                    )
                                  } else {
                                    return <p className="c-white text-sm">ไม่มีข้อมูลรางวัล</p>
                                  }
                                })()}
                              </div>
                            </a>
                          </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RulesGuideModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        scope={round ? {
          huayId: round.huayId,
          huayName: round.huayName,
          huayTypeId: round.huayTypeId,
          huayTypeName: round.huayTypeName,
        } : undefined}
        closeButtonLabel="ยอมรับและตกลง"
        fallbackRules={(
          <div className="c-dark">
            <h6 className="t-medium">กติกาการแทงหวย</h6>
            <p className="fs-14">
              1. ผู้เล่นสามารถแทงหวยได้ตามแพ็คเกจที่เลือก<br />
              2. ตรวจสอบอัตราจ่ายและส่วนลดก่อนแทง<br />
              3. ห้ามแทงหลังเวลาปิดรับ<br />
              4. ผลการแทงถือเป็นที่สิ้นสุด<br />
              5. กรณีมีข้อโต้แย้ง ให้ติดต่อเจ้าหน้าที่
            </p>

            <h6 className="t-medium mt-3">วิธีการเล่น</h6>
            <p className="fs-14">
              1. เลือกแพ็คเกจที่ต้องการ<br />
              2. กรอกตัวเลขที่ต้องการแทง<br />
              3. ระบุจำนวนเงินที่ต้องการแทง<br />
              4. กดปุ่มยืนยันเพื่อส่งโพย<br />
              5. รอผลการออกรางวัล
            </p>

            <h6 className="t-medium mt-3">ข้อควรระวัง</h6>
            <p className="fs-14">
              - ตรวจสอบความถูกต้องของโพยทุกครั้ง<br />
              - เก็บหลักฐานการแทงไว้<br />
              - ห้ามใช้โปรแกรมช่วยแทง<br />
              - แจ้งปัญหาทันทีหากพบความผิดปกติ
            </p>
          </div>
        )}
        fallbackGuides={(
          <div className="mt-4">
            <p className="text-center"><strong><u>วิธีการดูผลรางวัล "{round?.huayName || 'หวยรัฐบาลไทย'}"</u></strong></p>
            <div className="mt-3 mb-4 text-center">
              <p className="mb-1">ใช้รางวัลที่ 1 ในการออกผลรางวัล เช่น 107387</p>
              <p className="mb-1">สามตัวบน (387) สองตัวล่าง (08)</p>
              <p className="mb-1">สามตัวล่าง (091,298,716,602)</p>
            </div>
            <p className="mb-3 text-center"><strong><u>การตรวจผลรางวัล</u></strong></p>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <tbody>
                  <tr><td style={{ width: '100px' }}>3 ตัวบน</td><td>387</td></tr>
                  <tr><td>2 ตัวบน</td><td>87</td></tr>
                  <tr><td>2 ตัวโต๊ด</td><td>08 80 87 78</td></tr>
                  <tr><td>3 ตัวโต๊ด</td><td>387 378 837 873 738 783</td></tr>
                  <tr><td>วิ่งบน</td><td>7 - 8 - 3</td></tr>
                  <tr><td>ปักหลักร้อย</td><td>3</td></tr>
                  <tr><td>ปักหลักสิบ</td><td>8</td></tr>
                  <tr><td>ปักหลักหน่วย</td><td>7</td></tr>
                  <tr><td>2 ตัวล่าง</td><td>08</td></tr>
                  <tr><td>วิ่งล่าง</td><td>8 - 0</td></tr>
                  <tr><td>4 ตัวบน</td><td>7387</td></tr>
                  <tr><td>4 ตัวโต๊ด</td><td>7387 สลับตำแหน่งทั้ง 4 หลัก</td></tr>
                  <tr><td>5 ตัวโต๊ด</td><td>07387 สลับตำแหน่งทั้ง 5 หลัก</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      />
    </div>
  )
}

export default CommonTermsPage

